import { useEffect, useRef, useState } from 'react';

import useStore from 'Hooks/useStore';
import { useIntl } from 'react-intl';
import { useOptionalMessage } from 'Hooks/useOptionalMessage';
import { WizardStepHeader } from '../Shared/WizardStepHeader';
import { CargoEntity, CargoTypeEntity, TowOnTypeEntity } from 'Models/Entities';
import { SecuredGroups } from 'Models/Security/UserGroups';
import { dataToFerryTripBookingDto } from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { FerryBookingCreationDto } from 'Models/Dtos/BookingDtos';
import { FetchCargoTabPricingData, FetchVehiclePricingData } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { fetchUserById } from 'Services/Api/_HumanWritten/UserService';
import { getLowerCaseMessageByKey, getPascalCaseMessageByKey } from 'Stores/LanguageStore';
import { getMeasurementValueFromId } from 'Util/_HumanWritten/MeasurementUtils';
import { isNotNullOrUndefined, isNullOrUndefined } from 'Util/TypeGuards';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import If from 'Views/Components/If/If';
import SecuredPage from 'Views/Components/Security/SecuredPage';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { VehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { useWizardFetchedDataStore } from '../../BookingWizardState/BookingWizardFetchedData';
import { SelectedTrips } from '../../BookingWizardWrap';
import { useVehicleTabLogic } from './useVehicleTabLogic';
import { VehicleTabProvider } from './VehicleTabContext';
import { convertToFetchVehiclePricingRequest } from './VehicleTabHelpers';
import { VehicleTabInner } from './VehicleTabInner';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

export interface VehicleTabProps {
	isCustomer: boolean;
	selectedTrips: SelectedTrips;
}

export function VehicleTab({ isCustomer, selectedTrips }: VehicleTabProps) {
	const previousCargoRef = useRef<{ previous: boolean }>({ previous: false });
	const previousCargo = previousCargoRef.current;
	const store = useStore();
	const intl = useIntl();
	const fmtOptional = useOptionalMessage();
	const { wizardData, setWizardData, oldWizardData, innerWizardErrors, setInnerWizardErrors } =
		useBookingWizardDataStore();

	const [fetched, setFetched] = useState<boolean>(false);
	const [vehicleMeasurementPrices, setVehicleMeasurementPrices] = useState<{ [key: string]: number }>({});
	const [trailerMeasurementPrices, setTrailerMeasurementPrices] = useState<{ [key: string]: number }>({});

	const [cargoTypes, setCargoTypes] = useState<CargoTypeEntity[]>([]);
	const [towOnTypes, setTowOnTypes] = useState<TowOnTypeEntity[]>([]);
	const [prices, setPrices] = useState<{ [id: string]: number }>({});
	const [trailerPrices, setTrailerPrices] = useState<{ [id: string]: number }>({});

	const [previousCargoList, setPreviousCargoList] = useState<CargoEntity[]>([]);

	const { vehicleTabData, fetchVehicleTabData } = useWizardFetchedDataStore(x => ({
		vehicleTabData: x.vehicleTabData,
		fetchVehicleTabData: x.fetchVehicleTabData,
	}));

	// Update width pricing everytime length changes
	const oldVehicleLengthId = useRef('');
	const oldTrailerLengthId = useRef('');
	useEffect(() => {
		if (wizardData.innerWizardData.type !== 'vehicle') {
			return;
		}

		const shouldFetch =
			(wizardData.innerWizardData.vehicleLengthId ?? '') !== oldVehicleLengthId.current ||
			(wizardData.innerWizardData.trailerLengthId ?? '') !== oldTrailerLengthId.current;

		if (shouldFetch) {
			const request = convertToFetchVehiclePricingRequest(wizardData);
			FetchVehiclePricingData(request).then(res => {
				if (res?.vehicleWidthPricing ?? res?.widthPricing) {
					setPrices(res?.vehicleWidthPricing ?? res?.widthPricing ?? {});
				}
				if (res?.trailerWidthPricing) {
					setTrailerPrices(res.trailerWidthPricing);
				}
			});

			oldVehicleLengthId.current = wizardData.innerWizardData.vehicleLengthId ?? '';
			oldTrailerLengthId.current = wizardData.innerWizardData.trailerLengthId ?? '';
		}
	}, [wizardData]);

	useEffect(() => {
		const convertedWizardData: FerryBookingCreationDto | null = isNotNullOrUndefined(wizardData)
			? dataToFerryTripBookingDto(wizardData, false, false, false)
			: null;

		if (isNullOrUndefined(convertedWizardData)) {
			return;
		}

		FetchCargoTabPricingData(convertedWizardData).then(data => {
			setVehicleMeasurementPrices(data?.cargoMeasurementPrices ?? {});
			setTrailerMeasurementPrices(data?.towOnMeasurementPrices ?? {});
		});
		// Re-fetch when the trailer width changes (or is cleared): the trailer length prices
		// include the trailer's width multiplier, so they go stale when the width changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		(wizardData.innerWizardData as VehicleBookingWizardData).trailerWidthId,
		(wizardData.innerWizardData as VehicleBookingWizardData).showTrailerWidthDetails,
	]);

	useEffect(() => {
		if (isNotNullOrUndefined(vehicleTabData)) {
			setTowOnTypes(vehicleTabData.towOnTypes as TowOnTypeEntity[]);
			setPreviousCargoList((vehicleTabData.previousCargos ?? []) as CargoEntity[]);
			// Filter out disabled cargo types (should be filtered on the serverside... but just in case)
			let filteredCargoTypes = vehicleTabData.cargoTypes.filter(x => !x.disabled);

			if (!store.isStaff) {
				filteredCargoTypes = filteredCargoTypes.filter(
					x => !x.isRestricted || x.id === (wizardData.innerWizardData as VehicleBookingWizardData).cargoTypeId,
				);
			}
			setCargoTypes(filteredCargoTypes as CargoTypeEntity[]);

			setFetched(true);
		} else {
			const convertedWizardData: FerryBookingCreationDto | null = isNotNullOrUndefined(wizardData)
				? dataToFerryTripBookingDto(wizardData, false, false, false)
				: null;

			if (isNullOrUndefined(convertedWizardData)) {
				return;
			}

			fetchVehicleTabData(convertedWizardData);
		}
	}, [vehicleTabData]);

	useEffect(() => {
		const innerData = wizardData.innerWizardData;
		if (!(innerData instanceof VehicleBookingWizardData)) {
			return;
		}

		const cargoType = cargoTypes.find(x => x.id === innerData.cargoTypeId);

		// The assigned type has been filtered out of cargoTypes (e.g. it is now disabled). Do NOT
		// re-derive its length/weight or write wizardData here — this effect and VehicleTabInner's
		// de-select effect both key off [cargoTypes], and (as the parent) this one runs last, so
		// writing a stale copy of wizardData would overwrite the de-selection, leaving the disabled
		// type selected with no "vehicle type unavailable" notice. Leave de-selection to that effect.
		if (innerData.cargoTypeId && !cargoType) {
			return;
		}

		// Raise the length/weight to the vehicle type's minimum, and **only** when they are actually
		// below it. This used to write wizardData unconditionally, re-assigning the same values in
		// the else branches — a whole-object write built from this render's copy. Every effect in
		// VehicleTabInner runs before this one (children commit first), so an unconditional write
		// here silently reverted whatever they had just done: notably the visibility-rule reset in
		// useResetDisallowedMeasurements, which is how a length the operator's rules disallow stayed
		// selected — and therefore stayed in the dropdown, which always keeps its own selection.
		const nextLengthId =
			getMeasurementValueFromId(cargoType?.minimumLengthId) > getMeasurementValueFromId(innerData.vehicleLengthId)
				? cargoType?.minimumLengthId
				: innerData.vehicleLengthId;
		const nextWeightId =
			getMeasurementValueFromId(cargoType?.minimumWeightId) > getMeasurementValueFromId(innerData.vehicleWeightId)
				? cargoType?.minimumWeightId
				: innerData.vehicleWeightId;

		if (nextLengthId === innerData.vehicleLengthId && nextWeightId === innerData.vehicleWeightId) {
			return;
		}

		const newData = new BookingWizardData({ ...wizardData });
		if (newData.innerWizardData instanceof VehicleBookingWizardData) {
			newData.innerWizardData.vehicleLengthId = nextLengthId;
			newData.innerWizardData.vehicleWeightId = nextWeightId;
			setWizardData(newData);
		}
	}, [cargoTypes]);

	/* NEW: Added state for guest user check */
	const [isGuest, setIsGuest] = useState(false);
	const [isUserLoading, setIsUserLoading] = useState(true);

	useEffect(() => {
		if (wizardData.userId) {
			if (store.userId === wizardData.userId && store.userData) {
				setIsGuest(store.userData.isGuest ?? false);
				setIsUserLoading(false);
			} else {
				fetchUserById(wizardData.userId).then(user => {
					setIsGuest(user.isGuest ?? false);
					setIsUserLoading(false);
				});
			}
		}
	}, [wizardData.userId]);

	const refetchVehicleTabData = () => {
		const convertedWizardData: FerryBookingCreationDto | null = isNotNullOrUndefined(wizardData)
			? dataToFerryTripBookingDto(wizardData, false, false, false)
			: null;

		if (isNullOrUndefined(convertedWizardData)) {
			return;
		}

		fetchVehicleTabData(convertedWizardData);
	};

	// Extract logic hook
	useVehicleTabLogic(isCustomer);

	if (!(wizardData.innerWizardData instanceof VehicleBookingWizardData)) {
		// TODO BW - how to handle this?
		return <></>;
	}

	if (cargoTypes.length === 0) {
		return <LottieSpinner />;
	}

	const isCreateMode = wizardData.wizardMode === 'CREATE';

	const vehicleNounUpper = getPascalCaseMessageByKey('vehicle__name__label', 'Vehicle');
	const vehicleNounLower = getLowerCaseMessageByKey('vehicle__name__label', 'vehicle');
	let headerTitle: string;
	if (!isCreateMode) {
		headerTitle = intl.formatMessage(
			{
				id: 'vehicle-tab__title__edit',
				defaultMessage: 'Edit {noun} details',
			},
			{ noun: vehicleNounLower },
		);
	} else if (previousCargo.previous) {
		headerTitle = intl.formatMessage(
			{
				id: 'vehicle-tab__title__existing',
				defaultMessage: '{noun} details',
			},
			{ noun: vehicleNounUpper },
		);
	} else {
		headerTitle = intl.formatMessage(
			{
				id: 'vehicle-tab__title__new',
				defaultMessage: 'Enter your {noun} details',
			},
			{ noun: vehicleNounLower },
		);
	}
	const headerSubtitle = fmtOptional({
		id: 'vehicle-tab__subtitle',
		defaultMessage: `Tell us about the ${vehicleNounLower} travelling with you.`,
	});

	return (
		<SecuredPage groups={SecuredGroups.create.onlyLoggedIn().groups}>
			<div className="booking-wizard-tab add-vehicle-details-tab">
				<WizardStepHeader
					iconName="directions_car"
					title={headerTitle}
					subtitle={headerSubtitle || undefined}
					showCloseLink={!isCreateMode}
					bookingToEditId={wizardData?.bookingToEdit ?? ''}
				/>
				<If condition={wizardData.disabledVehicleType && wizardData.wizardMode === 'ALTERATION'}>
					<MessageBox
						title={intl.formatMessage({
							id: 'vehicle-tab__disabled-vehicle-type__header',
							defaultMessage: 'Vehicle type disabled',
						})}
						status="warning"
						slim
					>
						{intl.formatMessage({
							id: 'vehicle-tab__disabled-vehicle-type__body',
							defaultMessage:
								'The previous vehicle type you had selected is no longer available to be ' +
								'booked in the system. Please select a different vehicle type.',
						})}
					</MessageBox>
				</If>
				<VehicleTabProvider
					value={{
						previousCargo,
						cargoTypes,
						previousCargoList,
						setPreviousCargoList,
						refetchVehicleTabData,
						vehicleMeasurementPrices,
						trailerMeasurementPrices,
						towOnTypes,
						prices,
						trailerPrices,
						isGuest,
						isUserLoading,
					}}
				>
					<VehicleTabInner />
				</VehicleTabProvider>
			</div>
		</SecuredPage>
	);
}
